import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import tw from 'twin.macro';
import { UserContext } from 'contexts/ConfigContext';
import {
    calculateContainerHeight,
    calculateFontSize,
    formatNumWithKMBAlt,
    getConfig,
    getIntersectionOnApplyFilter,
    scaleMinValue,
} from 'utils/helperMethods';
import { FilterContextData } from 'contexts/FilterContext';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import InfiniteScroller from 'components/molecules/InfiniteScroller';
import { CustomSelect } from 'CustomComponentCss';
import DownSvg from 'assets/svg/Down';
import Linechart from 'components/molecules/Linechart';
import _ from 'lodash';
import { useApplyFilterMutation } from 'hooks/general/useApplyFilterMutation';
import { defaultApplyFilter } from 'components/pages/GrowthAnalysis';
import { useFetchTrendsChart } from 'hooks/opportunityIdentification/useFetchTrendsChart';
import { chartHeight, yAxisWidth } from 'components/template/PriceLadder/BarPointChartContainer';
import Loader from 'components/molecules/Loader';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import Legend from 'components/molecules/Legend';
import { trendsChartLegends } from 'components/template/PriceCurve/PriceCurveTrendsChartContainer';
import { subHeaderClass, yAxisLabelClass } from 'components/template/GrowthAnalysis/ExpandedView';
import { colorConstants } from 'config/colorConstants';
import { commonClassName } from 'components/pages/PromoScorecard';
import { useFetchChart2Data } from 'hooks/opportunityIdentification/useFetchChart2Data';
import Barchart from 'components/molecules/Barchart';
import { graphXAxisLabelFontSize } from 'components/organism/GrowthAnalysis/SalesAttributionDrilldownChart';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import {
    opportunityIdentificationChart2Download,
    opportunityIdentificationTrendChartDownload,
} from 'parser/graphToExcelDownloadParser';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    OPPORTUNITY_IDENTIFICATION_SUB_LEVELS_DROPDOWN_CHANGED,
    OPPORTUNITY_IDENTIFICATION_LEVELS_DROPDOWN_CHANGED,
} from 'utils/mixpanelEventNames';
import ElasticityGroupBarChart1 from './ElasticityGroupBarChart1';
import ElasticityGroupBarChart2 from './ElasticityGroupBarChart2';
import { useFetchAttributeLvlElasticityExport } from 'hooks/opportunityIdentification/useFetchAttributeLvlElasticityExport';
import { ConfigKeys } from 'utils/Enums';

const ContainerDiv = styled.div`
    ${tw`border border-borderLine  border-solid pb-4 flex-col rounded`}
`;

const LevelsFilterDiv = styled.div`
    ${tw`flex gap-2 items-center pt-4 pb-2`}
`;
const InnerDiv = styled.div`
    ${tw`relative flex py-5 items-center`}
`;

const stackedChartId = 'opportunity-identification-grouped-chart';

const barContainerHeight = Math.max(450, calculateContainerHeight(2, 33 + 16 * 2) - 200); //adding a buffer of 200
const yAxisGraphWidth = calculateFontSize(window.innerWidth, 140);

const OpportunityElasticityDriver: React.FC = () => {
    const { appConfig } = useContext(UserContext);
    const { decimalForNonPercentage } = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);

    const elasticityConfig = appConfig.opportunityIdentification.elasticityDriver;
    const [firstChartIntersectionFilters, setFirstChartIntersectionFilters] = useState<any>({});
    const [selectedLevel, setSelectedLevel] = useState(elasticityConfig.levels[0]);
    const [selectedSubLevel, setSelectedSubLevel] = useState(elasticityConfig.levels[1]);
    const [selectedFirstChartItem, setSelectedFirstChartItem] = useState<any>('');
    const [selectedSecondChartItem, setSelectedSecondChartItem] = useState<any>('');

    const {
        allDimAndAttributeTypeList,
        idForRestOfComponent,
        currentFrequency,
        timePeriodSelection,
        idForRestOfComponentLoading,
    } = useContext(FilterContextData);

    const { loading: applyFilterDataLoading1, applyFilterMutate: applyFilterDataMutate1 } = useApplyFilterMutation({
        onComplete: onApplyFilterComplete1,
    });

    const {
        loading: applyFilterDataLoading2,
        error: applyFilterDataError2,
        applyFilterMutate: applyFilterDataMutate,
    } = useApplyFilterMutation({ onComplete: onApplyFilterComplete2 });

    const {
        data: trendsData,
        loading: trendsDataLoading,
        error: trendsDataError,
        fetchTrendsChartData,
    } = useFetchTrendsChart();

    const {
        data: attributeLevelElasticityExportData,
        loading: attributeLevelElasticityExportLoading,
        error: attributeLevelElasticityExportError,
        fetchAttributeLevelElasticityExport,
    } = useFetchAttributeLvlElasticityExport();

    const {
        data: chart2Data,
        loading: chart2DataLoading,
        error: chart2DataError,
        fetchChart2Data,
    } = useFetchChart2Data();

    const offsetRef = useRef(0);
    const elasticityDriverChart1Ref = useRef<any>();
    const elasticityDriverTrendsChartRef = useRef<any>();
    const elasticityDriverChart2Ref = useRef<any>();

    useEffect(() => {
        if (!idForRestOfComponentLoading) {
            const filters = JSON.stringify(idForRestOfComponent);
            const vars = {
                filters: filters,
                timePeriod: String(timePeriodSelection).toLowerCase(),
                dimensionsList: ['dim1', 'dim2'],
                frequency: currentFrequency?.key,
                attributesList: [selectedLevel.value, selectedSubLevel.value],
            };
            fetchAttributeLevelElasticityExport({ variables: vars });
        }
    }, [
        idForRestOfComponent,
        idForRestOfComponentLoading,
        allDimAndAttributeTypeList?.loading,
        selectedLevel,
        selectedSubLevel,
        selectedFirstChartItem,
        selectedSecondChartItem,
    ]);

    useEffect(() => {
        setSelectedSecondChartItem('');
    }, [selectedFirstChartItem]);

    function onApplyFilterComplete1(payload: any) {
        const tempApplyFilterResponse = getIntersectionOnApplyFilter(idForRestOfComponent, payload);
        setFirstChartIntersectionFilters(tempApplyFilterResponse);
    }

    function onApplyFilterComplete2(payload: any) {
        const tempApplyFilterResponse = getIntersectionOnApplyFilter(firstChartIntersectionFilters, payload);
        parseAndFetchChart2Data(tempApplyFilterResponse);
        parseAndFetchTrendsChartData(tempApplyFilterResponse);
    }

    const variables = useMemo(() => {
        if (!idForRestOfComponentLoading) {
            const filters = JSON.stringify(idForRestOfComponent);
            return {
                filters: filters,
                timePeriod: String(timePeriodSelection).toLowerCase(),
                dimension: elasticityConfig.dimension,
                attributeType: selectedLevel.value,
                limit: elasticityConfig.limit,
                offset: elasticityConfig.offset,
            };
        }
    }, [
        idForRestOfComponent,
        idForRestOfComponentLoading,
        allDimAndAttributeTypeList?.loading,
        timePeriodSelection,
        selectedLevel.value,
    ]);

    useEffect(() => {
        offsetRef.current = 0;
    }, [variables]);

    // first apply filter call
    useEffect(() => {
        if (selectedFirstChartItem?.category) {
            const tempApplyFilterVariable: any = _.cloneDeep(defaultApplyFilter);
            let dim1Variables = {};
            let dim2Variables = {};
            if (selectedLevel.dimension === 'dim1') {
                dim1Variables = {
                    attributeExId: selectedFirstChartItem.category,
                    attributeDesc: selectedFirstChartItem.category,
                    attributeType: selectedLevel.value,
                    dimension: 'dim1',
                };
                tempApplyFilterVariable.jsonstring_dim1.inclusion = [dim1Variables];
            } else {
                dim2Variables = {
                    attributeExId: selectedFirstChartItem.category,
                    attributeDesc: selectedFirstChartItem.category,
                    attributeType: selectedLevel.value,
                    dimension: 'dim2',
                };
                tempApplyFilterVariable.jsonstring_dim2.inclusion = [dim2Variables];
            }

            tempApplyFilterVariable.jsonstring_dim2 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim2);
            tempApplyFilterVariable.jsonstring_dim1 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim1);
            applyFilterDataMutate1({ variables: tempApplyFilterVariable });
        }
    }, [selectedFirstChartItem, selectedLevel]);

    // second apply filter call
    useEffect(() => {
        if (selectedSecondChartItem?.category) {
            const tempApplyFilterVariable: any = _.cloneDeep(defaultApplyFilter);
            let dim1Variables = {};
            let dim2Variables = {};
            if (selectedSubLevel.dimension === 'dim1') {
                dim1Variables = {
                    attributeExId: selectedSecondChartItem.category,
                    attributeDesc: selectedSecondChartItem.category,
                    attributeType: selectedSubLevel.value,
                    dimension: 'dim1',
                };
                tempApplyFilterVariable.jsonstring_dim1.inclusion = [dim1Variables];
            } else {
                dim2Variables = {
                    attributeExId: selectedSecondChartItem.category,
                    attributeDesc: selectedSecondChartItem.category,
                    attributeType: selectedSubLevel.value,
                    dimension: 'dim2',
                };
                tempApplyFilterVariable.jsonstring_dim2.inclusion = [dim2Variables];
            }

            tempApplyFilterVariable.jsonstring_dim2 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim2);
            tempApplyFilterVariable.jsonstring_dim1 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim1);
            applyFilterDataMutate({ variables: tempApplyFilterVariable });
        }
    }, [selectedSecondChartItem]);

    useEffect(() => {
        setSelectedSecondChartItem({});
        setSelectedFirstChartItem({});
    }, [selectedLevel]);

    //function to fetch chart 2 data
    function parseAndFetchChart2Data(filter: any) {
        fetchChart2Data({
            variables: {
                timePeriod: String(timePeriodSelection).toLowerCase(),
                dimension: selectedSubLevel.dimension,
                attributeType: _.snakeCase(selectedSubLevel.value),
                filters: JSON.stringify(filter),
                frequency: currentFrequency?.key,
                limit: elasticityConfig?.chart2Limit,
                offset: 0,
            },
        });
    }

    //function to fetch chart 3 data
    function parseAndFetchTrendsChartData(filter: any) {
        fetchTrendsChartData({
            variables: {
                timePeriod: String(timePeriodSelection).toLowerCase(),
                dimension: selectedSubLevel.dimension,
                attributeType: _.snakeCase(selectedSubLevel.value),
                frequency: currentFrequency?.key,
                filters: JSON.stringify(filter),
                limit: 20, //limit is not used here, hence hardcoding
                offset: 0,
            },
        });
    }

    const infiniteScrollOnNext = (loading: boolean) => {
        if (loading) return;
        const newOffset = elasticityConfig.limit + offsetRef.current;
        offsetRef.current = newOffset;
    };

    const trendsChartExportData = useMemo(() => {
        return opportunityIdentificationTrendChartDownload(trendsData?.chartData?.data);
    }, [trendsData?.chartData?.data]);

    const chart2ExportData = useMemo(() => {
        return opportunityIdentificationChart2Download(
            chart2Data?.chartData?.result,
            selectedLevel?.label,
            elasticityConfig.legendMapping,
        );
    }, [chart2Data?.chartData?.result]);

    //graph main axis width
    const containerWidth = useContainerWidth({
        containerId: stackedChartId,
        buffer: yAxisWidth * 1 + 34 * 2,
    });

    const barHeight2 = Math.max(
        100,
        Array.isArray(chart2Data?.chartData?.result) && chart2Data?.chartData?.result?.length > 0
            ? barContainerHeight / chart2Data?.chartData?.result?.length
            : 0,
    );
    const chart2Width = containerWidth / 1 - 16 * 3; //subtract inner padding for 2 charts

    return (
        <div className="flex-col">
            <div className="flex justify-between items-center">
                <div className="flex gap-4 items-center">
                    <LevelsFilterDiv>
                        <span className="font-xs">Levels</span>
                        <CustomSelect
                            suffixIcon={<DownSvg />}
                            className="dropdown-height dropdown-width-alt uniqSelOILevelsDropdown"
                            value={selectedLevel}
                            options={elasticityConfig?.levels?.filter(
                                (item: any) => item.value != selectedSubLevel.value,
                            )}
                            onChange={(val: any, item: any) => {
                                setSelectedLevel(item);
                                trackEventMixpanel(OPPORTUNITY_IDENTIFICATION_LEVELS_DROPDOWN_CHANGED, {
                                    attributeTypeName: item?.label,
                                });
                            }}
                        />
                    </LevelsFilterDiv>
                    <LevelsFilterDiv>
                        <span className="font-xs">Sub Levels</span>
                        <CustomSelect
                            suffixIcon={<DownSvg />}
                            className="dropdown-height dropdown-width-alt uniqSelOILevelsDropdown"
                            value={selectedSubLevel}
                            options={elasticityConfig?.levels?.filter((item: any) => item.value != selectedLevel.value)}
                            onChange={(val: any, item: any) => {
                                setSelectedSubLevel(item);
                                setSelectedSecondChartItem('');
                                trackEventMixpanel(OPPORTUNITY_IDENTIFICATION_SUB_LEVELS_DROPDOWN_CHANGED, {
                                    attributeTypeName: item?.label,
                                });
                            }}
                        />
                    </LevelsFilterDiv>
                </div>
                <div className="flex gap-2 items-center">
                    <CopyToClipBoard refOfComponent={elasticityDriverChart1Ref} />
                    {!attributeLevelElasticityExportLoading && !attributeLevelElasticityExportError && (
                        <DownloadExcel
                            headers={attributeLevelElasticityExportData && attributeLevelElasticityExportData[0]}
                            excelData={attributeLevelElasticityExportData?.slice(1)}
                            title={'BasePriceElasticities'}
                        />
                    )}
                </div>
            </div>
            {/* 1. Infinite Chart Component */}
            <div className="flex gap-4" ref={elasticityDriverChart1Ref}>
                <div className="px-4.5 w-full">
                    <ElasticityGroupBarChart1
                        selectedChartItem={selectedFirstChartItem}
                        setSelectedChartItem={setSelectedFirstChartItem}
                        selectedLevel={selectedLevel}
                        loading={false}
                    />
                </div>
                <div className="px-4.5  w-full">
                    <ElasticityGroupBarChart2
                        selectedFirstChartItem={selectedFirstChartItem}
                        showError={selectedFirstChartItem === 'error'} // show error when first chart fails
                        selectedChartItem={selectedSecondChartItem}
                        setSelectedChartItem={setSelectedSecondChartItem}
                        selectedLevel={selectedSubLevel}
                        intersectionFilter={firstChartIntersectionFilters}
                        loading={applyFilterDataLoading1}
                    />
                </div>
            </div>

            {/* 2. DrillDown Component */}
            {selectedSecondChartItem?.category && (
                <>
                    <ContainerDiv className={`${commonClassName} !pt-0 mt-4`}>
                        <InnerDiv className={`${subHeaderClass} justify-between items-center`}>
                            <h2 className="mb-0">{'Elasticity division for ' + selectedSecondChartItem?.category}</h2>
                            <div className="flex gap-2 items-center">
                                <CopyToClipBoard refOfComponent={elasticityDriverChart2Ref} />
                                {!chart2DataLoading && !chart2DataError && (
                                    <DownloadExcel
                                        title={'OpportunityIdentificationChart2'}
                                        excelData={chart2ExportData.data}
                                        headers={chart2ExportData.headers}
                                    />
                                )}
                            </div>
                        </InnerDiv>

                        {chart2DataLoading || applyFilterDataLoading2 ? (
                            <div style={{ height: chartHeight + 50 }}>
                                <Loader />
                            </div>
                        ) : chart2DataError || applyFilterDataError2 ? (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: chartHeight + 50,
                                }}
                            >
                                <ErrorBox msg={String(chart2DataError || applyFilterDataError2)} />
                            </div>
                        ) : (
                            <div className="px-4.5" ref={elasticityDriverChart2Ref}>
                                <div className="flex justify-between items-center">
                                    <p
                                        style={{ width: yAxisGraphWidth, textAlign: 'center' }}
                                        className={`${yAxisLabelClass}`}
                                    >
                                        {selectedLevel?.label}
                                    </p>
                                    <Legend data={elasticityConfig.legendMapping} />
                                    <p></p>
                                </div>

                                <InfiniteScroller
                                    scrollThreshold={0.9}
                                    data={chart2Data?.chartData}
                                    loading={false}
                                    hasMore={false}
                                    height={barContainerHeight}
                                    onNext={() => {
                                        infiniteScrollOnNext(chart2DataLoading);
                                    }}
                                >
                                    {chart2Data?.chartData?.result?.map((item: any, index: number) => {
                                        return (
                                            <div className="relative flex items-center">
                                                <div
                                                    style={{
                                                        width: yAxisGraphWidth,
                                                        minWidth: yAxisGraphWidth,
                                                        overflowWrap: 'anywhere',
                                                        fontSize: graphXAxisLabelFontSize,
                                                    }}
                                                    className="text-center p-[2px]"
                                                >
                                                    {item?.category}
                                                </div>
                                                <Barchart
                                                    offsetXDistance={1}
                                                    padding={[0, 10, 0, 10]}
                                                    barWidth={30}
                                                    dataset={[item]}
                                                    height={barHeight2}
                                                    loading={false}
                                                    chartContainer={`opportunity-identification-chart-2-${index}`}
                                                    fields={['category', 'sales']}
                                                    intervalLabelTooltipKey={'sales'}
                                                    minBound={scaleMinValue(20, chart2Data?.chartData?.min)}
                                                    maxBound={chart2Data?.chartData?.max}
                                                    width={chart2Width - yAxisGraphWidth}
                                                    transpose
                                                    showValueInTheMiddle
                                                    reflect
                                                    tooltipFormatter={(xAxis, val) => {
                                                        return {
                                                            name: xAxis,
                                                            value: formatNumWithKMBAlt(
                                                                val,
                                                                decimalForNonPercentage,
                                                                decimalForNonPercentage,
                                                            ),
                                                        };
                                                    }}
                                                    intervalFormatter={(val: any) => {
                                                        return formatNumWithKMBAlt(
                                                            val,
                                                            decimalForNonPercentage,
                                                            decimalForNonPercentage,
                                                        );
                                                    }}
                                                    Xline={false}
                                                    colorKey={'color'}
                                                />
                                            </div>
                                        );
                                    })}
                                </InfiniteScroller>
                                <Barchart
                                    additionalStyle={{ marginLeft: yAxisGraphWidth - 10 }}
                                    padding={[0, 10, 20, 10]}
                                    dataset={[]}
                                    height={50}
                                    loading={false}
                                    chartContainer={`opportunity-identification-chart-x-2-${1}`}
                                    fields={['category', 'sales']}
                                    intervalLabelTooltipKey={'sales'}
                                    minBound={scaleMinValue(20, chart2Data?.chartData?.min)}
                                    maxBound={chart2Data?.chartData?.max}
                                    width={chart2Width - yAxisGraphWidth + 20}
                                    transpose
                                    reflect
                                    yAxisLabelFormatter={(val: any) =>
                                        formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage)
                                    }
                                    YGrid={false}
                                    xAxisConfig={false}
                                    colorKey={'color'}
                                />
                                <p className={`text-center relative ${yAxisLabelClass}`}>Elasticity </p>
                            </div>
                        )}
                    </ContainerDiv>

                    {/* 3. TREND CHART  */}
                    {selectedSecondChartItem?.category && (
                        <ContainerDiv className={`${commonClassName} !pt-0 mt-4  `}>
                            <InnerDiv className="justify-between">
                                <h2 className={`${subHeaderClass} mb-0`}>
                                    {'Price Elasticity Trend chart for ' + selectedSecondChartItem?.category}
                                </h2>
                                <div className="flex gap-2 items-center">
                                    <CopyToClipBoard refOfComponent={elasticityDriverTrendsChartRef} />
                                    {!trendsDataLoading && !trendsDataError && (
                                        <DownloadExcel
                                            title={'OpportunityIdentificationTrendsChart'}
                                            excelData={trendsChartExportData.data}
                                            headers={trendsChartExportData.headers}
                                        />
                                    )}
                                </div>
                            </InnerDiv>
                            <div className="px-4.5" ref={elasticityDriverTrendsChartRef}>
                                <>
                                    {trendsDataLoading || applyFilterDataLoading2 ? (
                                        <div style={{ height: chartHeight + 50 }}>
                                            <Loader />
                                        </div>
                                    ) : trendsDataError || applyFilterDataError2 ? (
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                height: chartHeight + 50,
                                            }}
                                        >
                                            <ErrorBox msg={String(trendsDataError || applyFilterDataError2)} />
                                        </div>
                                    ) : (
                                        <>
                                            <Legend data={trendsChartLegends} />
                                            <p className={`${yAxisLabelClass} mb-4`}>Elasticity</p>
                                            <div className="ml-1 flex">
                                                <Linechart
                                                    padding={[0, 0, 12, 0]}
                                                    xAxisConfig={false}
                                                    YGrid={false}
                                                    dataset={[]}
                                                    height={chartHeight}
                                                    loading={false}
                                                    chartContainer={`opportunity-identification-trends-chart-y`}
                                                    fields={['period', 'Year Ago', 'Current Year']}
                                                    minBound={trendsData?.chartData?.minVal}
                                                    maxBound={trendsData?.chartData?.maxVal}
                                                    width={yAxisWidth} //subtracting the padding
                                                    xAxisLabelFormatter={(val: any) => {
                                                        return val;
                                                    }}
                                                    yAxisLabelFormatter={(val: any) => {
                                                        return formatNumWithKMBAlt(
                                                            val,
                                                            decimalForNonPercentage,
                                                            decimalForNonPercentage,
                                                        );
                                                    }}
                                                    groupedChartColor={[colorConstants.lightBlue, colorConstants.pink]}
                                                    colorKey={'color'}
                                                    disableIntervalLabel
                                                />
                                                <Linechart
                                                    dataset={trendsData?.chartData?.data || []}
                                                    height={chartHeight}
                                                    loading={false}
                                                    chartContainer={`opportunity-identification-trends-chart-main`}
                                                    fields={['period', 'Year Ago', 'Current Year']}
                                                    minBound={trendsData?.chartData?.minVal}
                                                    maxBound={trendsData?.chartData?.maxVal}
                                                    width={containerWidth || window.innerWidth} //subtracting the padding
                                                    xAxisLabelFormatter={(val: any) => {
                                                        return val;
                                                    }}
                                                    groupedChartColor={[colorConstants.lightBlue, colorConstants.pink]}
                                                    colorKey={'color'}
                                                    disableIntervalLabel
                                                    tooltipFormatter={(xAxis, val, from) => {
                                                        return {
                                                            name: from,
                                                            value: formatNumWithKMBAlt(
                                                                val,
                                                                decimalForNonPercentage,
                                                                decimalForNonPercentage,
                                                            ),
                                                        };
                                                    }}
                                                />
                                            </div>
                                            <p className={`${yAxisLabelClass} text-center`}>Time</p>
                                        </>
                                    )}
                                </>
                            </div>
                        </ContainerDiv>
                    )}
                </>
            )}
        </div>
    );
};

export default OpportunityElasticityDriver;
